import React from 'react';
import { RubricCategory, SimpleMapping } from './mappingTypes';
import { getMappingStyles } from './mappingStyles';
import { useDarkMode } from '../../../context/DarkModeContext';

interface RubricSelectorProps {
  categories: RubricCategory[];
  mappings: SimpleMapping[];
  selectedGoalId: string | null;
  onToggleMapping: (categoryId: string, itemId: string) => void;
  onSelectAll?: (categoryId: string, selectAll: boolean) => void;
  onSelectAllGlobal?: (selectAll: boolean) => void;
  className?: string;
}

const RubricSelector: React.FC<RubricSelectorProps> = ({
  categories,
  mappings,
  selectedGoalId,
  onToggleMapping,
  onSelectAll,
  onSelectAllGlobal,
  className = ''
}) => {
  const { darkMode } = useDarkMode();
  const styles = getMappingStyles(darkMode);

  const isItemMapped = (categoryId: string, itemId: string): boolean => {
    if (!selectedGoalId) return false;
    const mapping = mappings.find(m => 
      m.goalId === selectedGoalId && m.rubricCategory === categoryId
    );
    return mapping ? mapping.rubricItems.includes(itemId) : false;
  };

  const getCategoryMappedCount = (categoryId: string): number => {
    if (!selectedGoalId) return 0;
    const mapping = mappings.find(m => 
      m.goalId === selectedGoalId && m.rubricCategory === categoryId
    );
    return mapping ? mapping.rubricItems.length : 0;
  };

  const getTotalMappedCount = (): number => {
    if (!selectedGoalId) return 0;
    return mappings
      .filter(m => m.goalId === selectedGoalId)
      .reduce((count, m) => count + m.rubricItems.length, 0);
  };

  const getTotalItemsCount = (): number => {
    return categories.reduce((sum, cat) => sum + cat.items.length, 0);
  };

  return (
    <div className={`${styles.sectionBg} rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-base font-semibold ${styles.textColor}`}>Company Evaluation Criteria</h3>
        {selectedGoalId && onSelectAllGlobal && (
          <button
            onClick={() => {
              const totalMapped = getTotalMappedCount();
              const totalItems = getTotalItemsCount();
              onSelectAllGlobal(totalMapped !== totalItems);
            }}
            className={`text-sm ${styles.linkColor} transition-colors`}
          >
            {getTotalMappedCount() === getTotalItemsCount() ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </div>
      <div className="space-y-6">
        {categories.map(category => {
          const mappedCount = getCategoryMappedCount(category.id);
          const allSelected = mappedCount === category.items.length;
          
          return (
            <div key={category.id}>
              <div className="flex items-center justify-between mb-2">
                <h4 className={`text-sm font-medium ${styles.mutedTextColor} flex items-center gap-2`}>
                  {category.name}
                  {mappedCount > 0 && (
                    <span className={`text-xs font-normal ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      ({mappedCount}/{category.items.length})
                    </span>
                  )}
                </h4>
                {selectedGoalId && onSelectAll && (
                  <button
                    onClick={() => onSelectAll(category.id, !allSelected)}
                    className={`text-xs ${styles.linkColor} transition-colors`}
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              <div className="space-y-1">
                {category.items.map(item => {
                  const isChecked = isItemMapped(category.id, item.id);
                  
                  return (
                    <label
                      key={item.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                        styles.hoverBg
                      } ${!selectedGoalId ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => selectedGoalId && onToggleMapping(category.id, item.id)}
                        disabled={!selectedGoalId}
                        className={`w-4 h-4 ${styles.checkboxColor} rounded focus:ring-2 focus:ring-blue-500`}
                      />
                      <span className={`text-sm ${styles.textColor}`}>{item.text}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RubricSelector;